/**
 * Is the meeting's signaling socket actually alive?
 *
 * A WebSocket only reports `close` when the TCP connection says so. When the
 * laptop's network drops and comes back on a different IP — Wi-Fi to hotspot,
 * one access point to another, a carrier NAT that forgot the mapping — nothing
 * ever says so: the socket stays OPEN, every frame we send sits unacknowledged
 * in the kernel's retransmit queue, and Linux keeps retrying for ~15 minutes
 * before giving up. Measured 2026-10-13 with tools/meeting_driver.py: after a
 * Wi-Fi drop that came back on a new IP, the signaling socket was still "open"
 * with 1032 bytes unsent and nothing received for 325 s, while media had long
 * since recovered — no presence, chat, active speaker or host controls.
 *
 * The heartbeat was already being sent every 30 s; its reply was never read.
 * This reads it: every heartbeat must be answered within a deadline, and one
 * that is not means the socket is dead whatever its readyState says.
 *
 * The SFU control socket (hooks/sfu-socket.ts) has the same failure and uses
 * the same monitor, with its own `ping` request and timings.
 */

export const HEARTBEAT_INTERVAL_MS = 30_000
/** How long a routine heartbeat may go unanswered. */
export const HEARTBEAT_TIMEOUT_MS = 10_000
/** Tighter, for the probe sent when the browser says it is back online. */
export const ONLINE_PROBE_TIMEOUT_MS = 5_000

export type LivenessOptions = {
  /** Send a Phoenix heartbeat under this ref. False if it could not be sent. */
  sendHeartbeat: (ref: string) => boolean
  nextRef: () => string
  /** The socket missed its deadline — close it and reconnect. Called at most once. */
  onDead: (detail: { reason: string; waitedMs: number }) => void
  intervalMs?: number
  timeoutMs?: number
  setTimeoutFn?: (fn: () => void, ms: number) => unknown
  clearTimeoutFn?: (handle: unknown) => void
  setIntervalFn?: (fn: () => void, ms: number) => unknown
  clearIntervalFn?: (handle: unknown) => void
  /**
   * Milliseconds on a monotonic clock. Defaults to `performance.now()`, the
   * clock the deadline timers themselves run on: comparing deadlines on the
   * wall clock instead let a clock change — a laptop waking, an NTP step —
   * stop the `online` probe pulling a deadline in, or push one out.
   */
  now?: () => number
}

export type LivenessMonitor = {
  /** A reply arrived for this heartbeat ref. */
  ack: (ref: string | null) => void
  /**
   * Something — anything — arrived on the socket. A socket that is delivering
   * frames is alive whichever beat they answer, and counting them matters
   * under load: a server busy enough to answer one heartbeat late is still
   * pushing everything else, and replacing its sockets then (every client at
   * once, each reconnect a fresh join and snapshot) is how a slow server is
   * turned into a down one.
   */
  heard: () => void
  /** Probe now, with its own deadline (e.g. on the browser's `online` event). */
  probe: (reason: string, timeoutMs?: number) => void
  stop: () => void
}

/**
 * Start heartbeating. Call once the socket is open; `stop()` when it closes.
 *
 * Only one probe is outstanding at a time: a routine beat that comes due while
 * one is still waiting does not push its deadline back, so a dead socket is
 * caught within interval + timeout however the beats line up. A tighter probe
 * (the online one) can pull the deadline in, never out.
 */
export function createLivenessMonitor(opts: LivenessOptions): LivenessMonitor {
  const intervalMs = opts.intervalMs ?? HEARTBEAT_INTERVAL_MS
  const timeoutMs = opts.timeoutMs ?? HEARTBEAT_TIMEOUT_MS
  const setT = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms))
  const clearT = opts.clearTimeoutFn ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const setI = opts.setIntervalFn ?? ((fn, ms) => setInterval(fn, ms))
  const clearI = opts.clearIntervalFn ?? ((h) => clearInterval(h as ReturnType<typeof setInterval>))
  const now = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()))

  let stopped = false
  /** Refs of heartbeats sent and not yet answered. Any answer proves the socket. */
  const outstanding = new Set<string>()
  let deadline: unknown = null
  let deadlineAt = 0
  let sentAt = 0

  const clearDeadline = () => {
    if (deadline !== null) clearT(deadline)
    deadline = null
    deadlineAt = 0
  }

  const stop = () => {
    if (stopped) return
    stopped = true
    clearI(interval)
    clearDeadline()
    outstanding.clear()
  }

  const probe = (reason: string, waitMs = timeoutMs) => {
    if (stopped) return
    const ref = opts.nextRef()
    // A heartbeat that cannot even be written is a socket already closing; its
    // own close event handles that, so do not arm a deadline for it.
    if (!opts.sendHeartbeat(ref)) return
    outstanding.add(ref)
    const at = now()
    if (deadline !== null && deadlineAt <= at + waitMs) return
    if (deadline === null) sentAt = at
    clearDeadline()
    deadlineAt = at + waitMs
    deadline = setT(() => {
      deadline = null
      if (stopped || outstanding.size === 0) return
      const waitedMs = Math.round(now() - sentAt)
      stop()
      opts.onDead({ reason, waitedMs })
    }, waitMs)
  }

  const ack = (ref: string | null) => {
    if (stopped || ref === null || !outstanding.has(ref)) return
    // The socket answered, so every earlier unanswered beat was just slow.
    outstanding.clear()
    clearDeadline()
  }

  const heard = () => {
    if (stopped || outstanding.size === 0) return
    outstanding.clear()
    clearDeadline()
  }

  const interval = setI(() => probe('heartbeat'), intervalMs)

  return { ack, heard, probe, stop }
}
